import { Grid, Typography } from '@mui/material';

import { BffWorkingHours } from '@/types/generated';
import { getLocalTimeFromTimeZone } from '@/utils/getLocalTimeFromTimeZone';

import { getNextOpenTime, getTodayCloseTime, isOfficeOpen } from './LocationStatus.helpers';

export interface ILocationStatus {
	bffWorkingHours: BffWorkingHours[];
	holidayHoursMessageText?: string;
	timeZoneId?: string;
	brandName?: string;
	mockTime?: {
		localDayOfWeek: string;
		localTime: string;
		localAmOrPm: string;
	};
}

export default function LocationStatus({
	bffWorkingHours,
	holidayHoursMessageText,
	timeZoneId,
	brandName,
	mockTime,
}: ILocationStatus) {
	const localCurrenTimeDetails = mockTime ?? getLocalTimeFromTimeZone(timeZoneId || '');

	const { localDayOfWeek, localTime, localAmOrPm } = localCurrenTimeDetails;
	const hours = bffWorkingHours?.filter((hour) => hour.date === localDayOfWeek);
	const isOfficeOpenNow = isOfficeOpen(hours, localTime, localAmOrPm);
	const closeTime = getTodayCloseTime(hours);
	const nextOpenTime = getNextOpenTime(localDayOfWeek, bffWorkingHours, localAmOrPm);
	const shouldShowWorkingHours = closeTime || nextOpenTime;

	const isClearChoice = brandName === 'ClearChoice';
	const showClosedLabel = !isClearChoice && !isOfficeOpenNow;

	return (
		<Grid container>
			{shouldShowWorkingHours && (
				<Grid item container gap={0.5} justifyContent="left" data-testid="location-status-section">
					{/* When center is either open or closed - Label: "Open ·" or "Closed ·" (ClearChoice doesn't show "Closed") */}
					{(isOfficeOpenNow || showClosedLabel) && (
						<Grid item>
							<Typography
								variant="bodySmallBold"
								color={isOfficeOpenNow ? 'success.dark' : 'error.dark'}
								data-testid={
									isOfficeOpenNow ? 'location-status-current-open' : 'location-status-current-closed'
								}
							>
								{isOfficeOpenNow ? 'Open' : 'Closed'} &middot;
							</Typography>
						</Grid>
					)}

					<Grid item>
						{isOfficeOpenNow ? (
							<Typography
								variant="bodySmallBook"
								color="text.primary"
								data-testid="location-status-current-open-text"
							>
								Closes at {closeTime}
							</Typography>
						) : nextOpenTime ? (
							<Typography
								variant="bodySmallBook"
								color="text.primary"
								data-testid="location-status-current-closed-text"
							>
								Opens at {nextOpenTime}
							</Typography>
						) : null}
					</Grid>
				</Grid>
			)}
			{holidayHoursMessageText && (
				<Grid
					item
					container
					justifyContent="left"
					sx={{ paddingBottom: 1 }}
					data-testid="location-status-holiday-text"
				>
					<Typography variant="lowestLevelBook" color="text.tertiary">
						{holidayHoursMessageText}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
}
